"use client";

import FeatureTab from "@/app/(core)/(home)/components/FeatureTab";
import HomeVideoEmbed from "@/app/(core)/(home)/components/HomeVideoEmbed";
import SectionHeader from "@/app/(core)/components/SectionHeader";
import { Box, Grid, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import classes from "./more-than-resource-section.module.css";
import PageSection from "./PageSection";

const MOBILE_BREAKPOINT = "md";

const PLACEHOLDER_VIMEO_IDS = {
  questions: "1166304744",
  tracking: "1166304681",
  syllabus: "1166304719",
  builder: "1166304694",
} as const;

const FEATURES = [
  {
    id: "questions",
    title: "Interactive Questions",
    description:
      "Review answers alongside examiner mark schemes. Student-led marking ensures you learn what earns marks, and what doesn't.",
  },
  {
    id: "tracking",
    title: "Performance Tracking",
    description:
      "Advanced analytics track your progress across all resources. Compare your performance to other users, or IB exam results.",
  },
  {
    id: "syllabus",
    title: "Syllabus Checklist",
    description:
      "Tick-off topics and sub-topics as you complete them. Update your progress site-wide so you can stay on track for your exams.",
  },
  {
    id: "builder",
    title: "Custom Test Builder",
    description:
      "Create tailored mock-exam papers with your choice of sub-topic, difficulty, and question format. Perfect for exam preparation.",
  },
];

interface MoreThanResourceSectionProps {
  title?: string;
  subTitle?: string;
  firstSection?: boolean;
}

export default function MoreThanResourceSection({
  firstSection = false,
  title,
  subTitle,
}: MoreThanResourceSectionProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints[MOBILE_BREAKPOINT]})`
  );
  const [activeFeature, setActiveFeature] = useState(FEATURES[0].id);
  const featuresScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobile) return;
    const wrapper = featuresScrollRef.current;
    if (!wrapper) return;

    const onScroll = () => {
      const top = wrapper.offsetTop;
      const height = wrapper.offsetHeight;
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const scrollRange = height - viewportHeight;
      if (scrollY < top || scrollY > top + scrollRange) return;
      const progress = Math.max(0, Math.min(1, (scrollY - top) / scrollRange));
      const index = Math.min(
        Math.floor(progress * FEATURES.length),
        FEATURES.length - 1
      );
      setActiveFeature(FEATURES[index].id);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  const sectionContent = (
    <PageSection
      variant="gray"
      firstSection={firstSection}
      minHeight={false}
      className={classes.features}
    >
      <SectionHeader
        title={title ?? "More Than Just A Resource Site."}
        subTitle={
          subTitle ??
          "The only IB platform with integrated performance tracking, syllabus completion monitoring, and intelligent learning management."
        }
        align="center"
      />

      {/* Mobile: horizontal scroll tabs then preview */}
      <Box className={classes.mobileTabsWrap}>
        <Box className={classes.mobileTabsScroll}>
          <Box className={classes.mobileTabsInner}>
            {FEATURES.map((f) => (
              <Box
                key={f.id}
                className={clsx(
                  classes.mobileTabPill,
                  activeFeature === f.id
                    ? classes.mobileTabPillActive
                    : classes.mobileTabPillInactive
                )}
                onClick={() => setActiveFeature(f.id)}
                component="button"
                type="button"
              >
                {f.title}
              </Box>
            ))}
          </Box>
        </Box>
        <Box className={classes.featuresPreview}>
          <HomeVideoEmbed
            activeFeature={activeFeature}
            features={FEATURES}
            vimeoIds={PLACEHOLDER_VIMEO_IDS}
          />
        </Box>
      </Box>

      {/* Desktop: side-by-side tabs + preview */}
      <Grid
        className={clsx(classes.featuresContent, classes.desktopContent)}
        gutter={60}
      >
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Box className={classes.featuresTabs}>
            {FEATURES.map((f) => (
              <FeatureTab
                key={f.id}
                title={f.title}
                description={f.description}
                isActive={activeFeature === f.id}
                onClick={() => setActiveFeature(f.id)}
                lightBg
              />
            ))}
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Box className={classes.featuresPreview}>
            <HomeVideoEmbed
              activeFeature={activeFeature}
              features={FEATURES}
              vimeoIds={PLACEHOLDER_VIMEO_IDS}
            />
          </Box>
        </Grid.Col>
      </Grid>
    </PageSection>
  );

  return (
    <div
      ref={featuresScrollRef}
      className={classes.featuresScrollWrap}
      style={{
        height: isMobile ? "auto" : `${FEATURES.length * 100}vh`,
      }}
    >
      <div className={classes.featuresSticky}>{sectionContent}</div>
    </div>
  );
}
